import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Board from '../common/board';
import ShoppingListCard from './shopping_list_card';
import ShoppingListDetail from './shopping_list_detail';
import NewShoppingListGroupForm from './new_shopping_list_group_form';
import NewShoppingListGroupButton from './new_shopping_list_group_button';
import ShoppingListGroupHeader from './shopping_list_group_header';
import { useIsMobile } from '../common/layout_hooks';
import {
  useCreateProductsBoardMutation,
  useDeleteShoppingListMutation,
  useGetShoppingListsByProductsBoardQuery,
  useLazyListProductsBoardQuery,
  useListProductsBoardQuery,
  useUpdateProductsBoardMutation,
  useUpdateShoppingListMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { useTracker } from '../../operations/tracking_operations';
import {
  boardAndShoppingListsWithShoppingListGroupDeleted,
  boardWithShoppingListGroupAdded,
  boardWithShoppingListGroupDragged,
  boardWithShoppingListGroupRenamed,
  boardWithShoppingListMoved,
  productsBoardWithShoppingListGroupsReplaced,
  shoppingListGroupWithShoppingListRemoved,
  shoppingListWithNewActivityRecord,
} from '../../services/products_board';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { ensureDefaultProductsBoardExists } from '../../operations/shopping_lists_operations';

const labels = {
  'Add another lane': '+ Add another group',
  'Click to add card': 'Add new shopping list',
  'Delete lane': 'Delete group',
  'Lane actions': 'Group actions',
  button: {
    'Add lane': 'Add group',
    'Add card': 'Add shopping list',
    Cancel: 'Cancel',
  },
  placeholder: {
    title: 'title',
    description: 'description',
    label: 'label',
  },
};

const customTranslation = (key) => labels[key];

const ProductsView = () => {
  const { data: userProfile } = useGetUserProfileQuery();

  const { data: productsBoard, isLoading: isProductsBoardLoading } =
    useListProductsBoardQuery();

  const { data: shoppingLists } = useGetShoppingListsByProductsBoardQuery(
    productsBoard?.id,
    {
      skip: !productsBoard?.id,
    },
  );

  const [updateProductsBoard] = useUpdateProductsBoardMutation();

  const [updateShoppingList] = useUpdateShoppingListMutation();

  const [deleteShoppingList] = useDeleteShoppingListMutation();

  const tracker = useTracker();

  const reactTrelloData = {
    lanes: (productsBoard?.shoppingListGroups || []).map(
      (shoppingListGroup) => ({
        id: shoppingListGroup.id,
        title: shoppingListGroup.title,
        disallowAddingCard: true,
        cards: (shoppingListGroup?.shoppingListIDs || [])
          .map((shoppingListID) => {
            const shoppingList = (shoppingLists || {})[shoppingListID];
            if (!shoppingList) {
              // Shopping list not yet loaded
              return null;
            }
            return {
              id: shoppingList.id,
              title: shoppingList.title,
              description: shoppingList.shortDescription || '',
            };
          })
          .filter((cardData) => !!cardData),
      }),
    ),
  };

  const navigate = useNavigate();

  const onCardClick = (cardId) => {
    navigate(`/products/${productsBoard.id}/shoppingList/${cardId}`);
  };

  const afterCardDelete = async (cardId, laneId) => {
    await deleteShoppingList(cardId);
    const shoppingListGroup = productsBoard.shoppingListGroups.find(
      (g) => g.id === laneId,
    );
    const newShoppingListGroup = shoppingListGroupWithShoppingListRemoved(
      shoppingListGroup,
      shoppingListId,
    );
    const newProductsBoard = productsBoardWithShoppingListGroupsReplaced(
      productsBoard,
      [newShoppingListGroup],
    );
    await updateProductsBoard(newProductsBoard);
  };

  const afterLaneAdd = async (params) => {
    const { id, title } = params;
    if (title && title.length > 0) {
      const newProductsBoard = boardWithShoppingListGroupAdded(
        productsBoard,
        id,
        title,
      );
      await updateProductsBoard(newProductsBoard);
    }
  };

  const afterLaneDelete = async (laneId) => {
    const { shoppingListIDs } = productsBoard.shoppingListGroups.find(
      (g) => g.id === laneId,
    );
    await Promise.all(shoppingListIDs.map(deleteShoppingList));
    const { productsBoard: updatedProductsBoard } =
      boardAndShoppingListsWithShoppingListGroupDeleted(
        productsBoard,
        [],
        laneId,
      );
    await updateProductsBoard(updatedProductsBoard);
  };

  const afterLaneUpdate = async (laneId, data) => {
    console.log({ laneId, data });
    // Only the title can be edited
    const updatedTitle = data.title;
    if (updatedTitle && updatedTitle.length > 0) {
      const updatedProductsBoard = boardWithShoppingListGroupRenamed(
        productsBoard,
        laneId,
        updatedTitle,
      );
      await updateProductsBoard(updatedProductsBoard);
    }
  };

  const afterLaneDrag = async (removedIndex, addedIndex, payload) => {
    const laneId = payload.id;
    const updatedProductsBoard = boardWithShoppingListGroupDragged(
      productsBoard,
      laneId,
      removedIndex,
      addedIndex,
    );
    await updateProductsBoard(updatedProductsBoard);
  };

  const afterCardDrag = async (
    cardId,
    sourceLaneId,
    targetLaneId,
    position,
    /* cardDetails, */
  ) => {
    const updatedProductsBoard = boardWithShoppingListMoved(
      productsBoard,
      cardId,
      sourceLaneId,
      targetLaneId,
      position,
    );
    await updateProductsBoard(updatedProductsBoard);
    // Cancel the drop, because the board will re-render with the new structure.
    return false;
  };

  let shoppingListDetailVisibleFor = null;
  const { shoppingListId } = useParams();
  if (shoppingListId) {
    shoppingListDetailVisibleFor = shoppingListId;
  }

  const isMobile = useIsMobile();
  const heightCssRule = isMobile
    ? { height: 'calc(var(--app-height) - 132px)' }
    : { height: 'calc(var(--app-height) - 60px)' };

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const [lazyListProductsBoard] = useLazyListProductsBoardQuery();

  const [createProductsBoard] = useCreateProductsBoardMutation();

  useEffect(() => {
    if (currentSpaceMembership && !isProductsBoardLoading && !productsBoard) {
      ensureDefaultProductsBoardExists(
        currentSpaceMembership.id,
        lazyListProductsBoard,
        createProductsBoard,
      );
    }
  }, [
    createProductsBoard,
    currentSpaceMembership,
    isProductsBoardLoading,
    lazyListProductsBoard,
    productsBoard,
  ]);

  return (
    <div className="smorg-board-container" id="products-board">
      <Board
        editable
        draggable
        editLaneTitle
        canAddLanes
        data={reactTrelloData}
        components={{
          Card: ShoppingListCard,
          NewLaneForm: NewShoppingListGroupForm,
          NewLaneSection: NewShoppingListGroupButton,
          LaneHeader: ShoppingListGroupHeader,
        }}
        onCardClick={onCardClick}
        onCardDelete={afterCardDelete}
        onLaneAdd={afterLaneAdd}
        onLaneDelete={afterLaneDelete}
        onLaneUpdate={afterLaneUpdate}
        handleDragEnd={afterCardDrag}
        handleLaneDragEnd={afterLaneDrag}
        t={customTranslation}
        style={{
          fontFamily: 'Helvetica',
          textAlign: 'left',
          ...heightCssRule,
        }}
      />
      <ShoppingListDetail
        shoppingListId={shoppingListDetailVisibleFor}
        visible={!!shoppingListDetailVisibleFor}
        onShopEvent={async (vendorName, occurredOn, itemIds) => {
          tracker(
            userProfile,
            [{ name: 'Products shopped', args: { vendorName } }],
            ['numShops'],
          );
          const updatedShoppingList = shoppingListWithNewActivityRecord(
            shoppingLists[shoppingListId],
            'SHOPPED',
            occurredOn,
            vendorName,
            itemIds,
          );
          await updateShoppingList(updatedShoppingList);
        }}
        dismiss={() => navigate(-1)}
      />
    </div>
  );
};

export default ProductsView;
